import React from 'react';
import { Link } from 'react-router-dom';
import { Global, css } from '@emotion/react';
import styled from '@emotion/styled/macro';

import { MONO, PortalStyles, useFonts, useTheme } from '../theme';
import { Icon } from '../ui/Icon';
import { SUPPORT_EMAIL } from './constants';
import type { TRule } from './rules';

/**
 * The sign-in screens' frame and pieces (prototype "StrengthX Coach Sign In",
 * restyled to redesign 3b board R24 and kit §3/§4 "Sign in"): the portal's
 * tokens and theme, the logo top-left, one 480px card with hairline inputs
 * (--bg fill inside the card) and an ink button, every target at least 44px.
 * The flow of every screen is unchanged.
 */

/** Sign-in pages scroll (the portal's root is a fixed 100vh). No amber in 3b: warnings use the theme's neutral --warn alias. */
const SignInStyles = () => (
  <Global
    styles={css`
      .sx.sx-signin {
        height: auto;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }
      .sx.sx-signin a.sx-underline {
        color: var(--tx);
        text-decoration: none;
        box-shadow: inset 0 -1px 0 var(--line2);
        position: relative;
      }
      /* Text links stay small but get a 44px target. */
      .sx.sx-signin a.sx-underline::after {
        content: '';
        position: absolute;
        inset: -12px -4px;
      }
      .sx.sx-signin a.sx-underline:hover {
        color: var(--tx2);
      }
      /* The wordmark is white; dark ink on the light theme. */
      .sx.sx-signin[data-theme='light'] .sx-logo {
        filter: invert(1);
      }
    `}
  />
);

const Header = styled('header')`
  height: 64px;
  flex: none;
  padding: 0 16px 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  & img {
    height: 20px;
    width: auto;
    display: block;
  }
`;

const ThemeButton = styled('button')`
  width: 44px;
  height: 44px;
  border-radius: var(--r-btn);
  border: 0;
  background: transparent;
  color: var(--tx);
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    background: var(--row);
  }
`;

const Main = styled('main')`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 6vh 20px 40px;
`;

/** One card (board R24): --card, 1px --line, 12px corners, 40px 32px inside. */
const Column = styled('div')`
  width: 100%;
  max-width: 480px;
  padding: 40px 32px;
  border-radius: var(--r-card);
  background: var(--card);
  border: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 18px;

  & form {
    display: contents;
  }

  @media (max-width: 520px) {
    padding: 28px 20px;
  }
`;

const Footer = styled('footer')`
  padding: 16px 24px 24px;
  display: flex;
  justify-content: center;
  text-align: center;
  font-size: 13px;
  color: var(--tx3);
`;

/** The page around every sign-in screen: theme switch, logo, footer. */
export const Frame = ({ children }: { children?: React.ReactNode }) => {
  const { theme, toggle } = useTheme();
  useFonts();
  return (
    <div className="sx sx-signin" data-theme={theme}>
      <PortalStyles />
      <SignInStyles />
      <Header>
        <img className="sx-logo" src="/assets/svg/logo-new.svg" alt="StrengthX" />
        <ThemeButton type="button" aria-label="Switch theme" onClick={toggle}>
          <Icon name={theme === 'dark' ? 'light_mode' : 'dark_mode'} size={21} />
        </ThemeButton>
      </Header>
      <Main>
        <Column>
          {children}
        </Column>
      </Main>
      <Footer>
        <span>
          Trouble signing in? <a className="sx-underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </span>
      </Footer>
    </div>
  );
};

type TTone = 'plain' | 'warn' | 'ok';

const toneColours: Record<TTone, [string, string]> = {
  plain: ['var(--row)', 'var(--tx)'],
  warn: ['var(--row)', 'var(--tx2)'],
  ok: ['var(--row)', 'var(--tx2)'],
};

const IconCircle = styled('span')<{ tone: TTone }>`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${(p) => toneColours[p.tone][0]};
  color: ${(p) => toneColours[p.tone][1]};
  display: grid;
  place-items: center;
`;

const Heading = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;

  & h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.2;
    text-wrap: pretty;
  }

  & p {
    margin: 0;
    font-size: 14px;
    color: var(--tx2);
    line-height: 1.55;
    text-wrap: pretty;
  }
`;

/** The optional icon circle, the title and the line under it. */
export const Title = ({ icon, tone = 'plain', title, sub }: { icon?: string; tone?: TTone; title: string; sub?: React.ReactNode }) => (
  <>
    {icon && (
      <IconCircle tone={tone}>
        <Icon name={icon} size={26} />
      </IconCircle>
    )}
    <Heading>
      <h1>{title}</h1>
      {sub && <p>{sub}</p>}
    </Heading>
  </>
);

const FieldBox = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;

  & .top {
    font-size: 12px;
    font-weight: 600;
    color: var(--tx2);
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
  }

  & .top label {
    flex: 1;
    min-width: 0;
  }

  & .aside {
    font-size: 12px;
    font-weight: 500;
    align-self: flex-start;
    padding: 2px 0;
  }

  .sx.sx-signin & a.aside.sx-underline {
    color: var(--tx3);
  }

  & .hint {
    font-size: 12px;
    color: var(--tx3);
    line-height: 1.45;
  }

  & .hint.bad {
    color: var(--err-tx);
  }
`;

const InputBox = styled('div')<{ big?: boolean; readOnly?: boolean; bad?: boolean }>`
  height: ${(p) => (p.big ? 58 : 44)}px;
  border-radius: var(--r-btn);
  border: 1px solid ${(p) => (p.bad ? 'var(--err)' : p.readOnly ? 'var(--line)' : 'var(--line2)')};
  background: ${(p) => (p.readOnly ? 'var(--row)' : 'var(--bg)')};
  display: flex;
  align-items: center;

  &:focus-within {
    border-color: ${(p) => (p.bad ? 'var(--err)' : 'var(--tx2)')};
    border-color: var(--tx);
  }

  & input {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0 14px;
    border: 0;
    background: transparent;
    outline: none;
    color: ${(p) => (p.readOnly ? 'var(--tx2)' : 'var(--tx)')};
    font-family: ${(p) => (p.big ? MONO : 'inherit')};
    font-size: ${(p) => (p.big ? 24 : 15)}px;
    letter-spacing: ${(p) => (p.big ? '.3em' : 'normal')};
  }

  & button {
    width: 44px;
    height: 44px;
    flex: none;
    border: 0;
    background: transparent;
    color: var(--tx2);
    display: grid;
    place-items: center;
    cursor: pointer;
  }
`;

type TFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange?: (value: string) => void;
  /** A link beside the label ("Forgot password?"). */
  aside?: { label: string; to: string };
  hint?: string;
  bad?: boolean;
  big?: boolean;
  readOnly?: boolean;
  /** Set for a password: whether it is shown, and the Show/Hide button. */
  password?: { shown: boolean; onToggle: () => void };
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  placeholder?: string;
  autoFocus?: boolean;
};

/** A labelled 48px field (58px for the code), with an optional Show/Hide button. */
export const Field = ({ id, label, value, onChange, aside, hint, bad, big, readOnly, password, inputMode, autoComplete, placeholder, autoFocus }: TFieldProps) => (
  <FieldBox>
    <div className="top">
      <label htmlFor={id}>{label}</label>
    </div>
    <InputBox big={big} readOnly={readOnly} bad={bad}>
      <input
        id={id}
        name={id}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        type={password && !password.shown ? 'password' : 'text'}
        readOnly={readOnly}
        inputMode={inputMode}
        autoComplete={autoComplete || 'off'}
        placeholder={placeholder}
        aria-invalid={bad ? 'true' : 'false'}
        aria-describedby={hint ? `${id}-hint` : undefined}
        autoFocus={autoFocus}
        spellCheck={false}
      />
      {password && (
        <button type="button" aria-label={password.shown ? 'Hide password' : 'Show password'} onClick={password.onToggle}>
          <Icon name={password.shown ? 'visibility_off' : 'visibility'} size={20} />
        </button>
      )}
    </InputBox>
    {hint && <span id={`${id}-hint`} className={bad ? 'hint bad' : 'hint'}>{hint}</span>}
    {/* Board R24: "Forgot password?" sits quietly under the field. */}
    {aside && <Link className="aside sx-underline" to={aside.to}>{aside.label}</Link>}
  </FieldBox>
);

const RuleList = styled('ul')`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;

  & li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
  }
`;

/** The password rules, ticked live. */
export const Rules = ({ rules }: { rules: TRule[] }) => (
  <RuleList aria-label="Password rules">
    {rules.map((rule) => (
      <li key={rule.label} style={{ color: rule.ok ? 'var(--tx2)' : 'var(--tx3)' }} data-ok={rule.ok ? 'true' : 'false'}>
        <Icon name={rule.ok ? 'check_circle' : 'radio_button_unchecked'} size={18} />
        <span>{rule.label}</span>
      </li>
    ))}
  </RuleList>
);

const CheckRow = styled('button')`
  width: 100%;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--tx);
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
  cursor: pointer;

  & .words {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  & .label {
    font-size: 14px;
    font-weight: 500;
    color: var(--tx);
  }

  & .sub {
    font-size: 12px;
    color: var(--tx2);
  }
`;

/** A checkbox row: the whole row is the target. */
export const Check = ({ checked, onChange, label, sub }: { checked: boolean; onChange: (checked: boolean) => void; label: string; sub?: string }) => (
  <CheckRow type="button" role="checkbox" aria-checked={checked} onClick={() => onChange(!checked)}>
    <Icon name={checked ? 'check_box' : 'check_box_outline_blank'} size={22} style={{ lineHeight: 1.05 }} />
    <span className="words">
      <span className="label">{label}</span>
      {sub && <span className="sub">{sub}</span>}
    </span>
  </CheckRow>
);

const Box = styled('div')`
  padding: 11px 12px;
  border-radius: var(--r-btn);
  font-size: 14px;
  line-height: 1.45;
  display: flex;
  gap: 8px;
`;

/** The red inline error: never a popup. */
export const ErrorBox = ({ children }: { children: React.ReactNode }) => (
  <Box role="alert" style={{ background: 'var(--err-soft)', color: 'var(--err-tx)', fontWeight: 500 }}>
    <Icon name="error" size={19} style={{ lineHeight: 1.2 }} />
    <span>{children}</span>
  </Box>
);

export const OkBox = ({ children }: { children: React.ReactNode }) => (
  <Box role="status" style={{ background: 'var(--row)' }}>
    <Icon name="check_circle" size={19} color="var(--tx2)" style={{ lineHeight: 1.2 }} />
    <span>{children}</span>
  </Box>
);

export const InfoBox = ({ children }: { children: React.ReactNode }) => (
  <Box style={{ background: 'var(--card)', border: '1px solid var(--line)', color: 'var(--tx2)', lineHeight: 1.5, display: 'block' }}>
    {children}
  </Box>
);

/** Error, ok and info boxes, each only when there is something to say. */
export const Messages = ({ error, ok, info }: { error?: string | null; ok?: string | null; info?: string | null }) => (
  <>
    {error ? <ErrorBox>{error}</ErrorBox> : null}
    {ok ? <OkBox>{ok}</OkBox> : null}
    {info ? <InfoBox>{info}</InfoBox> : null}
  </>
);

export const Actions = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const bigButton = (primary: boolean) => `
  height: 44px;
  border-radius: var(--r-btn);
  border: 1px solid ${primary ? 'var(--acc)' : 'var(--line2)'};
  background: ${primary ? 'var(--acc)' : 'transparent'};
  color: ${primary ? 'var(--acc-ink)' : 'var(--tx)'};
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  width: 100%;

  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const PrimaryBase = styled('button')`${bigButton(true)}`;
const SecondaryBase = styled('button')`${bigButton(false)}`;
const SecondaryAnchor = styled('a')`${bigButton(false)}`;
const PrimaryRouterLink = styled(Link)`${bigButton(true)}`;
const SecondaryRouterLink = styled(Link)`${bigButton(false)}`;

type TBigButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & { icon?: string; secondary?: boolean };

/** A 44px button: primary (ink, or the accent) or secondary (outlined). */
export const BigButton = ({ icon, secondary, children, type = 'button', ...rest }: TBigButtonProps) => {
  const Base = secondary ? SecondaryBase : PrimaryBase;
  return (
    <Base type={type} {...rest}>
      {icon && <Icon name={icon} size={20} />}
      {children}
    </Base>
  );
};

/** A 48px button that goes to another sign-in screen. */
export const BigLink = ({ to, icon, secondary, children }: { to: string; icon?: string; secondary?: boolean; children: React.ReactNode }) => {
  const Base = secondary ? SecondaryRouterLink : PrimaryRouterLink;
  return (
    <Base to={to}>
      {icon && <Icon name={icon} size={20} />}
      {children}
    </Base>
  );
};

/** A 48px outlined button that leaves the site (mail, app stores). */
export const BigAnchor = ({ href, icon, children, newTab }: { href: string; icon?: string; children: React.ReactNode; newTab?: boolean }) => (
  <SecondaryAnchor href={href} target={newTab ? '_blank' : undefined} rel={newTab ? 'noopener noreferrer' : undefined}>
    {icon && <Icon name={icon} size={20} />}
    {children}
  </SecondaryAnchor>
);

export const LinkRow = styled('div')`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  font-size: 14px;
`;

const textLink = `
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--tx);
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: var(--tx);
  text-underline-offset: 4px;
  display: inline-flex;
  align-items: center;

  &:disabled {
    color: var(--tx3);
    cursor: default;
  }
`;

const TextButtonBase = styled('button')`${textLink}`;
const TextRouterLink = styled(Link)`${textLink}`;

/** An underlined text action in the row under the buttons. */
export const TextButton = (props: React.ButtonHTMLAttributes<HTMLButtonElement>) => <TextButtonBase type="button" {...props} />;

export const TextLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <TextRouterLink to={to}>{children}</TextRouterLink>
);

const Card = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--card);
  border: 1px solid var(--line);

  & .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--row);
    border: 1px solid var(--line);
    display: grid;
    place-items: center;
    font-size: 13px;
    font-weight: 800;
    flex: none;
  }

  & .words {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  & .name {
    font-size: 14px;
    font-weight: 600;
  }

  & .sub {
    font-size: 13px;
    color: var(--tx2);
  }
`;

/** Who sent the invite. */
export const InviterCard = ({ initials, title, sub }: { initials: string; title: string; sub: string }) => (
  <Card>
    <span className="avatar" aria-hidden="true">{initials}</span>
    <div className="words">
      <span className="name">{title}</span>
      <span className="sub">{sub}</span>
    </div>
  </Card>
);
